import React, { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { FaInstagram, FaFacebookF, FaLinkedinIn } from "react-icons/fa"

function Header() {
	const headerRef = useRef(null)
	const navRef = useRef(null)
	const topStripRef = useRef(null)

	const [headerHeight, setHeaderHeight] = useState(0)
	const [navHeight, setNavHeight] = useState(0)
	const [topStripHeight, setTopStripHeight] = useState(0)
	const [showTopStrip, setShowTopStrip] = useState(true)
	const [lastScrollY, setLastScrollY] = useState(0)

	const isMobile = window.innerWidth < 768
	const extraSpace = isMobile ? 50 : 0

	// Measure top-strip height
	useEffect(() => {
		if (topStripRef.current) {
			setTopStripHeight(topStripRef.current.offsetHeight)
		}
	}, [isMobile])

	// Measure nav height
	useEffect(() => {
		if (navRef.current) {
			setNavHeight(navRef.current.offsetHeight)
		}
	}, [isMobile, showTopStrip, topStripHeight])

	// Measure header height (if needed for other calculations)
	useEffect(() => {
		const updateHeaderHeight = () => {
			if (headerRef.current) {
				setHeaderHeight(headerRef.current.offsetHeight)
			}
		}

		updateHeaderHeight()
		const timeoutId = setTimeout(updateHeaderHeight, 150)

		window.addEventListener("load", updateHeaderHeight)
		window.addEventListener("resize", updateHeaderHeight)
		return () => {
			clearTimeout(timeoutId)
			window.removeEventListener("resize", updateHeaderHeight)
			window.removeEventListener("load", updateHeaderHeight)
		}
	}, [])

	// Handle scroll for mobile to toggle top strip visibility
	useEffect(() => {
		if (!isMobile) return
		const handleScroll = () => {
			const currentScrollY = window.scrollY
			const threshold = 250
			if (currentScrollY >= threshold) {
				if (currentScrollY < lastScrollY) {
					setShowTopStrip(true)
				} else {
					setShowTopStrip(false)
				}
			} else {
				setShowTopStrip(true)
			}
			setLastScrollY(currentScrollY)
		}
		window.addEventListener("scroll", handleScroll)
		return () => window.removeEventListener("scroll", handleScroll)
	}, [lastScrollY, isMobile])

	// Compute visible header height for spacer adjustment (adding extraSpace for mobile)
	const visibleHeaderHeight = isMobile
		? navHeight + (showTopStrip ? topStripHeight + extraSpace : 0)
		: navHeight + topStripHeight

	return (
		<>
			<header
				ref={headerRef}
				className="site-header"
				style={{
					position: "fixed",
					// When the top strip is hidden, slide the header up by (topStripHeight - extraSpace*0.3) pixels so that less of the header is cut off.
					top: isMobile
						? showTopStrip
							? "0"
							: `-${topStripHeight - extraSpace * 0.3 - 10}px`
						: "0",
					left: "0",
					right: "0",
					transition: isMobile ? "top 0.4s ease-in-out" : undefined,
					zIndex: 1000,
				}}
			>
				{/* 1. TOP STRIP / SUB-HEADER */}
				<div
					className="top-strip"
					ref={topStripRef}
					style={
						isMobile
							? {
									overflow: "hidden",
									height: showTopStrip
										? `${topStripHeight + extraSpace}px`
										: "0px",
									transition: "height 0.4s ease-in-out",
							  }
							: {}
					}
				>
					<div className="top-strip-content">
						<div className="social-icons">
							<a
								href="https://www.instagram.com/shiftingsandsexp/"
								aria-label="Instagram"
								target="_blank"
								rel="noreferrer"
							>
								<FaInstagram size={20} />
							</a>
							<a
								href="https://www.facebook.com/profile.php?id=61564944561810"
								aria-label="Facebook"
								target="_blank"
								rel="noreferrer"
							>
								<FaFacebookF size={20} />
							</a>
							<a
								href="https://www.linkedin.com/company/shifting-sands-exp/"
								aria-label="LinkedIn"
								target="_blank"
								rel="noreferrer"
							>
								<FaLinkedinIn size={20} />
							</a>
						</div>
						<div className="phone-number">
							<a href="tel:778-999-3279" className="header-link">
								778-999-3279
							</a>
						</div>
					</div>
				</div>

				{/* 2. MAIN NAV AREA */}
				<nav className="nav-bar" ref={navRef}>
					<div className="logo">
						<Link to="/">
							<img
								src="/image/Shifting-Sands_Logo.png"
								alt="Shifting Sands Logo"
							/>
						</Link>
					</div>
					<div className="affiliate-logos">
						<div className="renshaw-affiliate">
							<div className="renshaw-affiliate-line">
								<p className="affiliate-line italic">
									An Independent Affiliate of
								</p>
								<img
									src="/image/renshaw-travel-logo.png"
									alt="Renshaw Travel"
									className="renshaw-logo"
								/>
							</div>
							<p className="bc-reg">B.C. Reg. No 2496</p>
						</div>
						<div className="virtuoso-affiliate">
							<div className="virtuoso-affiliate-line">
								<p className="affiliate-line italic">A proud</p>
								<img
									src="/image/virtuoso-logo.png"
									alt="Virtuoso"
									className="virtuoso-logo"
								/>
							</div>
							<p className="affiliate-line">Member</p>
						</div>
					</div>
				</nav>
			</header>
			{/* Spacer so that content isn’t covered */}
			<div style={{ height: visibleHeaderHeight }}></div>
		</>
	)
}

export default Header
